import React from 'react';
import {
  arrayOf,
  bool,
  func,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import { isFunc, PartTypes } from 'vdlib';
import { I18n as I } from 'react-redux-i18n';
import { Col, Divider, Row, Collapse, Checkbox } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import { filterData } from './utils';
import styles from './styles';

import TextFilter from 'components/tables/filters/TextFilter';
import DropdownFilter from 'components/tables/filters/DropdownFilter';
import DateFilter from 'components/tables/filters/DateFilter';

const { Panel } = Collapse;

export const BidTypesFilterName = 'BidTypes';
export const NoBidFilterValue = 'NoBid';

const AuctionsListFilters = props => {
  const {
    columnDefsKey,
    filters,
    isExpert,
    isInsurance,
    isOperator,
    lang,
    location,
    setFilter,
    showBidsCheckboxes,
  } = props;
  // returns a default filter if location filters correspond to location
  const extractFilter = React.useMemo(() => {
    if (!filters) return () => null;
    const { Value: locationFilterValue } =
      filters.find(x => x.Name === 'forLocation') || {};
    const defaultValueFormatter = value => value || null;
    return locationFilterValue === location.pathname
      ? (name, valueFormatter) => {
          const { Value: filterValue } =
            filters.find(f => f.Name === name) || {};
          return isFunc(valueFormatter)
            ? valueFormatter(filterValue)
            : defaultValueFormatter(filterValue);
        }
      : () => null;
  }, [filters, location]);

  const visibleFilters = React.useMemo(() => {
    const context = { columnDefsKey, isOperator, isInsurance, isExpert };

    return filterData
      .filter(f => f.visible(context))
      .map(({ visible, ...rest }) => rest);
  }, [isOperator, isInsurance, isExpert, columnDefsKey, lang]); //eslint-disable-line

  const { defaultBidTypes, bidTypes } = React.useMemo(() => {
    const bidTypes = [
      {
        value: PartTypes.New,
        label: (
          <i className="label label-success"> {I.t('PART_TYPE_NEW_PL')}</i>
        ),
      },
      {
        value: PartTypes.Used,
        label: (
          <i className="label label-danger"> {I.t('PART_TYPE_USED_PL')}</i>
        ),
      },
      {
        value: PartTypes.Imitation,
        label: (
          <i className="label label-warning"> {I.t('PART_TYPE_IMITATION')} </i>
        ),
      },
      {
        value: NoBidFilterValue,
        label: <i className="label label-info"> {I.t('NO_OFFER')} </i>,
      },
    ];
    return {
      defaultBidTypes: bidTypes.map(bt => bt.value),
      bidTypes,
    };
  }, [lang]); //eslint-disable-line

  const handleBidTypesChanged = React.useCallback(
    bidTypes => setFilter(BidTypesFilterName, bidTypes),
    [setFilter]
  );

  return (
    <Collapse>
      <Panel
        header={
          <div>
            <FilterOutlined style={styles.filterOutlined} />
            <div style={styles.title}>Filters</div>
          </div>
        }
        key="1"
      >
        <Row>
          <Col className="gutter-row" span={24}>
            <Row>
              {visibleFilters.map(({ name, optionsProp, type, ...rest }) => {
                const filterProps = {
                  defaultValue: extractFilter(name),
                  name,
                  onSetFilter: setFilter,
                  options: optionsProp ? props[optionsProp] : null,
                  ...rest,
                };
                const FilterComponent =
                  type === 'dropdown'
                    ? DropdownFilter
                    : type === 'date'
                    ? DateFilter
                    : TextFilter;
                return <FilterComponent key={name} {...filterProps} />;
              })}
            </Row>
          </Col>
          {showBidsCheckboxes && (
            <>
              <Divider style={styles.divider} />
              <Col
                className="gutter-row"
                span={7}
                style={styles.bidTypesLabelWrap}
              >
                <strong>Προσφορά:</strong>
              </Col>
              <Col className="gutter-row" span={17}>
                <Checkbox.Group
                  options={bidTypes}
                  defaultValue={defaultBidTypes}
                  onChange={handleBidTypesChanged}
                />
              </Col>
            </>
          )}
        </Row>
      </Panel>
    </Collapse>
  );
};

const lookupPropType = arrayOf(
  shape({
    Code: string,
    Description: string,
    ID: number,
  })
);

AuctionsListFilters.propTypes = {
  brands: lookupPropType,
  filters: arrayOf(shape({ Name: string, Value: oneOfType([string, object]) })),
  insurances: lookupPropType,
  isExpert: bool,
  isInsurance: bool,
  isOperator: bool,
  lang: string,
  location: shape({ pathname: string }),
  regions: lookupPropType,
  setFilter: func,
  showBidsCheckboxes: bool,
  vehicleTypes: lookupPropType,
};

export default AuctionsListFilters;
